import { forwardRef, type HTMLProps } from "react";
import { Button, ButtonProps } from "../../../components/Button";
import { useMergeRefs, useTrackingId } from "../../../hooks";
import { usePopoverContext } from "./internal/usePopoverContext";
import { childrenToString } from "../../../internal/functions";
import { DataTrackingId } from "../../../types";

/**
 * Props for the PopoverButton component.
 * @extends ButtonProps
 */
export type PopoverButtonProps = ButtonProps & DataTrackingId;

/**
 * PopoverButton component for creating button-style triggers.
 *
 * Features:
 * - Button styling and behavior with all Button props
 * - ARIA attributes and event handlers injected via getReferenceProps
 * - Automatic tracking ID generation for analytics
 *
 * @example
 * <Popover.Button>Click to open</Popover.Button>
 *
 * @example
 * <Popover.Button appearance="primary" size="large" icon={InfoIcon}>
 *   Information
 * </Popover.Button>
 */
export const PopoverButton = forwardRef<HTMLButtonElement, PopoverButtonProps>(
  function PopoverButton(props, ref) {
    const { children, ...rest } = props;
    const { refs, getReferenceProps, openState, triggerId } =
      usePopoverContext();

    const trackingId = useTrackingId({
      name: "PopoverButton",
      data: {
        children: childrenToString(props.children),
        appearance: props.appearance,
        icon: props.icon,
        size: props.size,
        type: props.type,
      },
      hasOverride: !!props["data-tracking-id"],
    });

    const buttonRef = useMergeRefs([refs.setReference, ref]);

    const buttonProps = {
      id: triggerId,
      "data-anv": "popover-button",
      "data-tracking-id": trackingId,
      "data-state": openState ? "open" : "closed",
      ...getReferenceProps(rest as HTMLProps<Element>),
      children,
    } as ButtonProps;

    return <Button ref={buttonRef} {...buttonProps} />;
  },
);

PopoverButton.displayName = "PopoverButton";
